import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { WorkflowInputModal } from '@/components/workflows/WorkflowInputModal';
import { WorkflowInputPanel } from '@/components/workflows/WorkflowInputPanel';
import { ProjectCard, type ProjectCardModel } from '@/components/projects/ProjectCard';
import apiClient from '../../services/apiClient';
import { getUserInfo } from '../../utils/auth';

import tasksV5Icon from '../../assets/icons/tasks-v5.png';

function formatRelativeTime(d: any) {
  try {
    const t = new Date(d).getTime();
    if (!Number.isFinite(t)) return '';
    const diffMins = Math.floor((Date.now() - t) / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffH = Math.floor(diffMins / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    return `${diffD}d ago`;
  } catch {
    return '';
  }
}

export default function TasksV5HomePage() {
  const navigate = useNavigate();

  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInputModal, setShowInputModal] = useState(false);
  const [myPanelError, setMyPanelError] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const res: any = await apiClient.get('/projects', { params: { includeCounts: 1 } } as any);
      const list = res?.data?.data || res?.data || [];
      setProjects(Array.isArray(list) ? list : []);
    } catch (e: any) {
      console.error('Failed to load projects:', e);
      toast.error(e?.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAll = useCallback(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // Enterprise-grade refresh: allow other panels to request a refresh, and refetch when tab becomes active.
  useEffect(() => {
    const onRefresh = () => refreshAll();
    const onFocus = () => refreshAll();
    const onVisibility = () => {
      try {
        if (document.visibilityState === 'visible') refreshAll();
      } catch (_) {
        refreshAll();
      }
    };

    window.addEventListener('workflow.tasks.refresh', onRefresh as any);
    window.addEventListener('project.tasks.refresh', onRefresh as any);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('workflow.tasks.refresh', onRefresh as any);
      window.removeEventListener('project.tasks.refresh', onRefresh as any);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshAll]);

  const sorted = useMemo(() => {
    return [...projects].sort((a, b) =>
      String(b?.updatedAt || '').localeCompare(String(a?.updatedAt || ''))
    );
  }, [projects]);

  const hasProjects = sorted.length > 0;

  return (
    <div className="h-screen w-full bg-white text-slate-900 dark:bg-neutral-950 dark:text-white flex flex-col">
      <div className="w-full px-6 py-6 max-w-none flex flex-col flex-1 min-h-0">
        {/* Header */}
        <div className="mb-6 border-b border-slate-200 pb-5 dark:border-neutral-800 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <img
                  src={tasksV5Icon}
                  alt="Tasks V5"
                  className="h-7 w-7 object-contain"
                  draggable={false}
                />
                <div className="text-2xl font-semibold text-slate-900 dark:text-white">Tasks V5</div>
              </div>
              <div className="mt-1 text-sm text-slate-500 dark:text-neutral-400">
                &ldquo;Projects, teams, decisions — one living map.&rdquo;
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 hover:bg-slate-50 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200 dark:hover:bg-neutral-900"
                onClick={() => void loadProjects()}
                disabled={loading}
              >
                Refresh
              </button>
              <button
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                onClick={() => setShowInputModal(true)}
              >
                + New Project
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {myPanelError ? (
            <div className="mb-4 w-full rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-100">
              <div className="font-semibold">Tasks couldn&apos;t be loaded</div>
              <div className="mt-1 text-xs opacity-90">{myPanelError}</div>
              <button
                className="mt-3 rounded-lg bg-rose-600 px-3 py-2 text-xs font-semibold text-white hover:bg-rose-700"
                onClick={() => void loadProjects()}
              >
                Retry
              </button>
            </div>
          ) : null}
          {!hasProjects ? (
            <div className="w-full">
              <WorkflowInputPanel
                title="Create a Project"
                subtitle="Upload files, use voice, or type your intent. We'll create the project and generate your workstreams."
                onCreated={(projectId: string) => navigate(`/tasks-v5/${encodeURIComponent(projectId)}`)}
              />
            </div>
          ) : (
            <div className="w-full">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {sorted.slice(0, 60).map((p) => {
                  const id = String(p?._id || p?.id || '');
                  const title = String(p?.title || 'Untitled Project');
                  const desc = String(p?.description || p?.teaser || '');
                  const ws = Number(p?.workstreamCount || 0) || 0;
                  const t = Number(p?.taskCount || 0) || 0;
                  const ai = Number(p?.actionItemCount || 0) || 0;
                  const updated = p?.updatedAt ? formatRelativeTime(p.updatedAt) : '';
                  const me = getUserInfo()?.id ? String(getUserInfo()?.id) : '';
                  const createdBy = p?.createdBy ? String(p.createdBy) : '';
                  const card: ProjectCardModel = {
                    id,
                    title,
                    description: desc,
                    updatedLabel: updated,
                    isCreator: !!me && !!createdBy && me === createdBy,
                    workstreamCount: ws,
                    taskCount: t,
                    actionItemCount: ai,
                    progressPct: p?.progressPct ?? null,
                    state: p?.state ?? null,
                    sealedAt: p?.sealedAt ?? null,
                    drifted: !!p?.drifted,
                    stalledSeverity: p?.stalledSeverity ?? null,
                  };
                  return (
                    <ProjectCard
                      key={id}
                      project={card}
                      onOpen={() => navigate(`/tasks-v5/${encodeURIComponent(id)}`)}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {showInputModal ? (
        <WorkflowInputModal
          onClose={() => setShowInputModal(false)}
          onCreated={(projectId: string) => {
            setShowInputModal(false);
            navigate(`/tasks-v5/${encodeURIComponent(projectId)}`);
          }}
        />
      ) : null}
    </div>
  );
}
